import type React from 'react'
import type { StationStatusSnapshot } from '@metro/station-status'
import { StationCardCarousel, type StationCardCarouselCard } from './StationCardCarousel'
import { IconMagnifier } from './icons/IconMagnifier'
import { defaultStationCardCopy, type StationCardCopySet } from './copy'
import styles from './StationCard.module.css'

/**
 * The bottom-overlay carousel host. Renamed conceptually from "card" to
 * "overlay": the actual card surface (header + From/To/Details) lives in
 * `StationCardView`; this wrapper anchors the carousel to the bottom
 * of the viewport with safe-area-aware insets.
 *
 * The single-card export is `StationCardCard`; the carousel slot
 * payload is `StationCardCarouselCard`. The two are aligned, so
 * `StationCardCard` is just a re-alias.
 */
export type StationCardCard = StationCardCarouselCard

export interface StationCardProps {
  cards: ReadonlyArray<StationCardCard>
  currentId: string
  onSettled: (id: string) => void
  /**
   * Triggered when the user taps the From action on a card. Production
   * buttons are gated behind `showRouteButtons`; pass a no-op when the
   * flag is off.
   */
  onFromTap: (stationId: string) => void
  /** As `onFromTap`, for the To action. */
  onToTap: (stationId: string) => void
  /** Triggered when the user taps the Station-information (details) action. */
  onDetailsTap: (stationId: string) => void
  /**
   * Forwarded to the inner carousel → per-slot views. Defaults to `true`
   * for shape-stability with isolated tests; production callers wire
   * this to the `routeBuilder` feature flag (in `StationCardOrDetailsRouter`)
   * so the buttons stay hidden until the flag rolls out.
   */
  showRouteButtons?: boolean
  /**
   * Fired when the user taps the status banner overlay on a Closed /
   * Restricted / Advisory card. Optional — no-op default. Routes to
   * DisruptionDetails (spec §6 `statusBannerTapped`).
   */
  onStatusBannerTap?: (stationId: string) => void
  /**
   * Opens the line's view on the map from the line link under the station
   * name (Figma LineView E). Only cards with a `lineHref` show the link.
   */
  onLineTap?: (stationId: string) => void
  /**
   * Fired when the user taps the Retry button in the Not-found empty
   * state. Optional — no-op default. Future destination: re-trigger
   * station fetch (deferred — see spec §6, output case
   * `notFoundRetryTapped`).
   */
  onNotFoundRetryTap?: () => void
  /**
   * Live status snapshot (naptanId-keyed) from the host's
   * StationStatusPreloader (spec §6). Each card resolves its own
   * status by looking up its `naptanId` in this snapshot. Absent key =
   * Open (no banner). Optional — defaults to empty (all Open).
   */
  statuses?: StationStatusSnapshot
  /**
   * Localised copy. Defaults to the English {@link defaultStationCardCopy};
   * the host injects a catalog-backed set. en values are byte-identical.
   */
  copy?: StationCardCopySet
}

/**
 * Compact-viewport bottom overlay. Pixel-parity with the StationCard V2
 * Figma spec (`metro/docs/superpowers/specs/2026-05-20-stationcard-v2-figma-pixelperfect.md`):
 *
 * - When `cards.length > 0`, mounts the carousel + per-slot
 *   `StationCardView`. Status overlays (Closed / Restricted) are not
 *   yet driven by carousel data — they remain a `StationCardView`
 *   prop, used by isolated snapshot fixtures only.
 * - When `cards.length === 0`, renders the **Not found** state per spec
 *   §2.2: centered VStack with a 56×56 magnifier, "Station not found"
 *   `boldPx20` title, and a full-width Retry PrimaryButton (h64, r18).
 *
 * Bottom inset uses `env(safe-area-inset-bottom, 0px)` to clear the
 * home indicator on iOS web view + still leave breathing room on
 * desktop (where the env value is 0).
 *
 * `pointer-events: none` on the wrapper + `pointer-events: auto` on the
 * carousel itself lets map taps pass through the side gutters around
 * the card while still receiving taps inside it.
 */
export function StationCard({
  cards,
  currentId,
  onSettled,
  onFromTap,
  onToTap,
  onDetailsTap,
  showRouteButtons = true,
  onStatusBannerTap,
  onLineTap,
  onNotFoundRetryTap,
  statuses,
  copy = defaultStationCardCopy,
}: StationCardProps): React.ReactElement {
  if (cards.length === 0) {
    return (
      <div data-testid="station-card-overlay" className={styles.overlay}>
        <div className={styles.carouselHost}>
          <article
            data-testid="station-card-not-found"
            className={styles.notFoundCard}
            role="status"
          >
            <IconMagnifier
              className={styles.notFoundIcon}
              data-testid="station-card-not-found-icon"
            />
            <h2 className={styles.notFoundTitle}>{copy.notFound}</h2>
            <button
              type="button"
              className={styles.notFoundRetry}
              data-testid="station-card-not-found-retry"
              onClick={onNotFoundRetryTap}
            >
              {copy.retry}
            </button>
          </article>
        </div>
      </div>
    )
  }
  return (
    <div data-testid="station-card-overlay" className={styles.overlay}>
      <div className={styles.carouselHost}>
        <StationCardCarousel
          cards={cards}
          currentId={currentId}
          onSettled={onSettled}
          onFromTap={onFromTap}
          onToTap={onToTap}
          onDetailsTap={onDetailsTap}
          showRouteButtons={showRouteButtons}
          onStatusBannerTap={onStatusBannerTap}
          onLineTap={onLineTap}
          statuses={statuses}
          copy={copy}
        />
      </div>
    </div>
  )
}
